import chartUrl from '@/assets/demo/recycling-chart.svg'
import type { FeedbackAnnotation, FeedbackCriterion, FeedbackInstance, FeedbackMergedNote, FixKind, TaskFeedback } from '@/features/results/feedback-types'

/**
 * Sample Writing content for the demo session (fictional student "Noura A.", fictional essays). Two tasks, each with:
 *  - the essay text (B2 level, with real spelling, agreement, collocation and cohesion problems),
 *  - the findings the grader would have made, written once and used twice: as the `/review/` evidence
 *    (`details.criteria` and `details.language_observations`, with the real server field names) and as the
 *    contract-shaped `/feedback/` payload (EN and AR notes, fix lines, instances, merged notes, next actions).
 *
 * Every offset is computed from the quote (never typed by hand), and a missing quote throws at module load, so a quote that
 * is not an exact substring of its essay cannot ship. demo-samples.test.ts re-checks the whole payload against the contract.
 *
 * Arabic follows the contract: Modern Standard Arabic, no grammatical gender (possessive ـك, unvocalised past tense,
 * nominal wording for fixes and actions), English words from the essay or chart in straight double quotes, Western digits,
 * no em dash.
 */

export const DEMO_CHART_URL: string = chartUrl

export type TaskNumber = 1 | 2
export type TaskBands = Record<FeedbackCriterion, number>

export const CRITERIA: readonly FeedbackCriterion[] = ['task_achievement', 'coherence_cohesion', 'lexical_resource', 'grammatical_range']
const LEDGER_CRITERIA = ['coherence_cohesion', 'lexical_resource', 'grammatical_range'] as const

type Fix = { kind: 'replace'; text: string } | { kind: 'instruction'; en: string; ar: string }

interface Finding {
  criterion: FeedbackCriterion
  kind: 'error' | 'gap' | 'strength'
  severity: 1 | 2 | 3
  /** Exact substring of the essay. */
  quote: string
  noteEn: string
  noteAr: string
  fix?: Fix
  /** Further places with the same kind of mistake, each with its own fix. */
  instances?: Array<{ quote: string; fix: Fix }>
  /** The quote of the primary finding this one is merged into (overlapping words). */
  alsoOf?: string
  /** Ledger text (band-blind, English), for `details.language_observations`. */
  observation: string
  effect?: string
}

interface Bilingual { en: string; ar: string }

export interface EssayContent {
  taskNumber: TaskNumber
  questionId: string
  paragraphs: string[]
  findings: Finding[]
  /** `details.criteria.<k>.evidence_quotes` (2 to 4 each). One is deliberately lower-case: the client re-finds quotes. */
  evidence: Record<FeedbackCriterion, string[]>
  /** `details.criteria.<k>.judgement`: the internal rationale. Never rendered to a student. */
  judgement: Record<FeedbackCriterion, string>
  reasons: Record<FeedbackCriterion, Bilingual>
  nextActions: Array<{ criterion: FeedbackCriterion; en: string; ar: string }>
  /** Server recommendation stems for the two weakest criteria (`details.feedback_recommendations`). */
  recommendations: string
  visualDataExtracted?: string
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

const cp = (text: string, utf16Index: number) => Array.from(text.slice(0, utf16Index)).length

/** Every literal occurrence of `quote` in `text`, as UTF-16 spans. */
function occurrences(text: string, quote: string): Array<{ start: number; end: number }> {
  const found: Array<{ start: number; end: number }> = []
  for (let at = text.indexOf(quote); at !== -1; at = text.indexOf(quote, at + 1)) found.push({ start: at, end: at + quote.length })
  return found
}

function spanOf(text: string, quote: string) {
  const [first] = occurrences(text, quote)
  if (!first) throw new Error(`demo writing sample: quote is not in the essay: ${quote}`)
  return { quote, start: first.start, end: first.end, start_cp: cp(text, first.start), end_cp: cp(text, first.end) }
}

export const essayText = (content: EssayContent) => content.paragraphs.join('\n\n')
export const wordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length

const fixFields = (fix: Fix | undefined): { fix_en: string | null; fix_ar: string | null; fix_kind: FixKind | null } =>
  !fix ? { fix_en: null, fix_ar: null, fix_kind: null }
    : fix.kind === 'replace' ? { fix_en: fix.text, fix_ar: fix.text, fix_kind: 'replace' }
      : { fix_en: fix.en, fix_ar: fix.ar, fix_kind: 'instruction' }

export const mean = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length
export const taskBand = (bands: TaskBands) => mean(CRITERIA.map((criterion) => bands[criterion]))

/* -------------------------------------------------------------------------- */
/* Task 1: the recycling chart                                                 */
/* -------------------------------------------------------------------------- */

export const TASK1: EssayContent = {
  taskNumber: 1,
  questionId: 'df-w-q1',
  paragraphs: [
    `The bar chart show the percentege of household waste that was recycled in four countries, Germany, Japan, Canada and Brazil, in 2005, 2015 and 2025.`,
    `In 2005, Germany had the highest rate of recycling at 48%, while Brazil was the lowest with only 9%. Japan and Canada was in the middle, with 21% and 27% respectivly. Germany continued to growing and it reached 67% in 2025, which is the highest figure on the chart.`,
    `The biggest change were in Japan. The rate of recicling in Japan increase from 21% in 2005 to 33% in 2015 and then reach 45% in 2025. Brazil also improve a lot, from 9% to 31%, but it was still the lowest country at the end. In contrast, Canada had only a small rise, from 27% to 36%, so the gap between Canada and Japan was became smaller.`,
    `Overall, all the countries recycled more waste at the end of the period, and Germany was always the leader.`,
  ],
  findings: [
    {
      criterion: 'grammatical_range', kind: 'error', severity: 2,
      quote: `The bar chart show`,
      noteEn: `A singular subject needs "shows", "Japan and Canada" needs "were", and the verb after "to" stays in its base form. These small slips repeat across your sentences and hold Grammatical Range and Accuracy back.`,
      noteAr: `المبتدأ المفرد يحتاج إلى "shows"، وعبارة "Japan and Canada" تحتاج إلى "were"، والفعل بعد "to" يبقى في صيغته الأصلية. تتكرر هذه الهفوات الصغيرة في الجمل وتحدّ من المدى النحوي والدقة.`,
      fix: { kind: 'replace', text: `The bar chart shows` },
      instances: [
        { quote: `Japan and Canada was`, fix: { kind: 'replace', text: `Japan and Canada were` } },
        { quote: `continued to growing`, fix: { kind: 'replace', text: `continued to grow` } },
      ],
      observation: `Verb forms slip in several places: subject-verb agreement and the form after "to".`,
      effect: `Meaning stays clear, but the reader notices the same kind of error again and again.`,
    },
    {
      criterion: 'lexical_resource', kind: 'error', severity: 2,
      quote: `percentege`,
      noteEn: `"percentege" is misspelt; the word is "percentage". Two more spelling slips appear later, and errors in everyday chart words make the reader stop and hold Lexical Resource back.`,
      noteAr: `كلمة "percentege" مكتوبة خطأً، والصحيح "percentage". ظهر خطآن إملائيان آخران لاحقًا، والأخطاء في كلمات الرسوم الشائعة توقف القارئ وتحدّ من الثروة اللغوية.`,
      fix: { kind: 'replace', text: `percentage` },
      instances: [
        { quote: `respectivly`, fix: { kind: 'replace', text: `respectively` } },
        { quote: `recicling`, fix: { kind: 'replace', text: `recycling` } },
      ],
      observation: `Common words are misspelt in three places.`,
      effect: `The reader pauses to decode words that should be automatic.`,
    },
    {
      criterion: 'task_achievement', kind: 'strength', severity: 1,
      quote: `In 2005, Germany had the highest rate of recycling at 48%, while Brazil was the lowest with only 9%.`,
      noteEn: `You pick the highest and lowest figures for the first year and compare them in one sentence. This is the kind of selection and comparison that the task asks for.`,
      noteAr: `اختيار أعلى رقم وأدنى رقم في السنة الأولى ومقارنتهما في جملة واحدة هو ما تطلبه المهمة.`,
      observation: ``,
    },
    {
      criterion: 'task_achievement', kind: 'error', severity: 2,
      quote: `reach 45% in 2025`,
      noteEn: `The chart shows 49% for Japan in 2025, not 45%. A wrong figure in a key comparison counts against Task Achievement even when the rest of the paragraph is accurate.`,
      noteAr: `يُظهر الرسم 49% لـ"Japan" في 2025 وليس 45%. الرقم الخاطئ في مقارنة أساسية يُحتسب على تحقيق المهمة حتى لو كانت بقية الفقرة دقيقة.`,
      fix: { kind: 'replace', text: `reached 49% in 2025` },
      observation: ``,
    },
    {
      criterion: 'lexical_resource', kind: 'error', severity: 1,
      quote: `a lot`,
      noteEn: `"a lot" is vague for a change from 9% to 31%. A precise word such as "substantially" or "sharply" shows the range of vocabulary that Lexical Resource rewards.`,
      noteAr: `عبارة "a lot" عامة جدًا لوصف تغير من 9% إلى 31%. كلمة دقيقة مثل "substantially" أو "sharply" تُظهر تنوع المفردات الذي يكافئه معيار الثروة اللغوية.`,
      fix: { kind: 'replace', text: `substantially` },
      observation: `A vague quantifier is used where a precise trend word would fit.`,
      effect: `The size of the change is less precise than the data allows.`,
    },
    {
      criterion: 'coherence_cohesion', kind: 'strength', severity: 1,
      quote: `In contrast, Canada had only a small rise, from 27% to 36%`,
      noteEn: `"In contrast" is used correctly here to set Canada's small rise against Japan's large one. Choosing linking words for their meaning, not only for their order, is what Coherence and Cohesion rewards.`,
      noteAr: `استخدام "In contrast" هنا صحيح لأنه يقابل الارتفاع الصغير في "Canada" بالارتفاع الكبير في "Japan". اختيار أدوات الربط بحسب المعنى لا بحسب الترتيب فقط هو ما يكافئه معيار التماسك والترابط.`,
      observation: `A contrast marker is chosen for its meaning and sets two trends against each other.`,
    },
    {
      criterion: 'coherence_cohesion', kind: 'error', severity: 2,
      quote: `so the gap between Canada and Japan was became smaller`,
      noteEn: `"so" suggests that Canada's small rise caused the gap to narrow. It did not: Japan's large rise did. Use "as a result" only for real results, or start a new sentence.`,
      noteAr: `توحي "so" بأن ارتفاع "Canada" الصغير هو سبب تقلص الفجوة، والسبب الحقيقي هو ارتفاع "Japan" الكبير. تُستخدم "as a result" للنتائج الفعلية فقط، أو يمكن البدء بجملة جديدة.`,
      fix: { kind: 'replace', text: `and the gap between Canada and Japan became smaller` },
      observation: `"so" links two ideas that are not cause and result.`,
      effect: `The reader reads a cause that the data does not support.`,
    },
    {
      criterion: 'grammatical_range', kind: 'error', severity: 1, alsoOf: `so the gap between Canada and Japan was became smaller`,
      quote: `was became smaller`,
      noteEn: `"was became" mixes two verb forms, and "became smaller" is enough. A slip like this at the end of a sentence stops it reading as secure.`,
      noteAr: `الجمع بين "was" و"became" يخلط صيغتين للفعل، ويكفي "became smaller". هذا الخلط في ختام الجملة يضعف انطباع الإتقان.`,
      fix: { kind: 'replace', text: `became smaller` },
      observation: `Two verb forms are combined in one verb phrase.`,
      effect: `Meaning stays clear but the sentence reads as an error.`,
    },
    {
      criterion: 'task_achievement', kind: 'gap', severity: 2,
      quote: `Overall, all the countries recycled more waste at the end of the period, and Germany was always the leader.`,
      noteEn: `Your overview is correct, but it comes last and gives only the general direction. An examiner looks for it early, with the main trend and the biggest changes named.`,
      noteAr: `الملخص العام صحيح لكنه جاء في النهاية ولا يذكر سوى الاتجاه العام. يبحث الممتحن عنه مبكرًا مع ذكر الاتجاه الرئيسي وأكبر التغييرات.`,
      fix: {
        kind: 'instruction',
        en: `Move the overview to the start and name the two biggest rises: Japan and Brazil.`,
        ar: `نقل الملخص العام إلى بداية المقال وذكر أكبر زيادتين: "Japan" و"Brazil".`,
      },
      observation: ``,
    },
  ],
  evidence: {
    task_achievement: [`In 2005, Germany had the highest rate of recycling at 48%`, `overall, all the countries recycled more waste at the end of the period`],
    coherence_cohesion: [`In contrast, Canada had only a small rise`, `Overall, all the countries recycled more waste`],
    lexical_resource: [`percentege`, `improve a lot`],
    grammatical_range: [`Japan and Canada was in the middle`, `so the gap between Canada and Japan was became smaller`],
  },
  judgement: {
    task_achievement: `Your response covers the three years and the four countries and makes clear comparisons. One reported figure does not match the chart, and the overview is placed after the details and stays general.`,
    coherence_cohesion: `Your paragraphs follow the chart in a clear order and use a contrast marker well. One linking word suggests a cause that the data does not show.`,
    lexical_resource: `Your vocabulary covers what the chart needs, but spelling errors in common words and a vague quantifier limit it.`,
    grammatical_range: `Your simple sentences are mostly accurate. Agreement and verb-form errors appear in many of the longer ones.`,
  },
  reasons: {
    task_achievement: { en: `You report the main figures accurately and include an overview, but one figure is wrong and the overview comes last.`, ar: `ذكرت الأرقام الرئيسية بدقة وأضفت ملخصًا عامًا، لكن أحد الأرقام خاطئ والملخص جاء في النهاية.` },
    coherence_cohesion: { en: `Your paragraphs follow the chart in a clear order, but one linking word does not match the meaning.`, ar: `فقراتك تتبع الرسم بترتيب واضح، لكن إحدى أدوات الربط لا تطابق المعنى.` },
    lexical_resource: { en: `Your vocabulary covers what the chart needs, but spelling errors and vague words like "a lot" limit it.`, ar: `مفرداتك تغطي ما يحتاجه الرسم، لكن أخطاء الإملاء والكلمات العامة مثل "a lot" تحدّ منها.` },
    grammatical_range: { en: `Your simple sentences are mostly correct, but agreement and verb-form errors appear in many longer sentences.`, ar: `جملك البسيطة صحيحة في معظمها، لكن أخطاء التطابق وصيغ الأفعال تظهر في كثير من الجمل الأطول.` },
  },
  nextActions: [
    {
      criterion: 'lexical_resource',
      en: `Before you finish, reread for spelling and replace vague words such as "a lot" with a precise verb or adverb.`,
      ar: `في المقال القادم: مراجعة الإملاء واستبدال الكلمات العامة مثل "a lot" بفعل أو ظرف أدق.`,
    },
    {
      criterion: 'grammatical_range',
      en: `Check that every verb matches its subject and that the verb after "to" stays in the base form.`,
      ar: `في المقال القادم: مطابقة كل فعل مع فاعله والتأكد من بقاء الفعل بعد "to" في صيغته الأصلية.`,
    },
  ],
  recommendations: `Include a clear overview of the main trends near the start and make sure every figure you report matches the visual. Proofread for agreement, spelling and verb-form errors before you finish.`,
  visualDataExtracted: `Household waste recycled (%). Germany 48, 56, 67. Japan 21, 33, 49. Canada 27, 30, 36. Brazil 9, 17, 31 (2005, 2015, 2025).`,
}

/* -------------------------------------------------------------------------- */
/* Task 2: public money and sporting events                                    */
/* -------------------------------------------------------------------------- */

export const TASK2: EssayContent = {
  taskNumber: 2,
  questionId: 'df-w-q2',
  paragraphs: [
    `Nowadays, many countries spend a huge amount of public money to host international sporting events like the Olympic Games or the World Cup. Some people think this money should be spent on local sports facilities instead. I partly agree with this opinion, and in this essay I will explain why.`,
    `On the one hand, local facilities give benefit to ordinary people every day. For example, a community in my city has a small football pitch which is used by children after school, and it keep them away from the streets. If the government build more parks and gyms, more people will do exercise and the country will have less health problems. In addition, these facilities is cheaper to maintain than a big stadium that is empty for most of the year.`,
    `On the other hand, international events can bring many advantages to a country. Firstly, thousands of tourists come to the city and spend money in hotels and restaurants, so the economy grows. Secondly, the events make the country famous, and this attract foreign companies to invest. Moreover, the new transport and stadiums are using again by the local people after the event finishes. For instance, Qatar build a new metro before the World Cup in 2022, and many residents still use it today.`,
    `However, I believe that the money must be shared more fairly. The governments can host big events but they must also spend enough on small clubs and schools, because young athletes can not become champions without good places to train.`,
    `In conclusion, both sides have good points, but local sports facilities are more important for ordinary people in the long term.`,
  ],
  findings: [
    {
      criterion: 'lexical_resource', kind: 'strength', severity: 1,
      quote: `spend a huge amount of public money to host international sporting events`,
      noteEn: `Topic vocabulary such as "public money", "host" and "international sporting events" is accurate and fits the question, which shows the reader that you understand the topic from the first line.`,
      noteAr: `مفردات الموضوع مثل "public money" و"host" و"international sporting events" دقيقة ومناسبة للسؤال، وهذا يُظهر للقارئ فهم الموضوع منذ السطر الأول.`,
      observation: `Topic vocabulary is accurate and well matched to the question.`,
    },
    {
      criterion: 'task_achievement', kind: 'strength', severity: 1,
      quote: `I partly agree with this opinion, and in this essay I will explain why.`,
      noteEn: `Your position is stated in the introduction, so the examiner knows your answer to the question from the start. Keep this habit and make sure the conclusion repeats the same position.`,
      noteAr: `موقفك معلن في المقدمة، فيعرف الممتحن إجابتك عن السؤال منذ البداية. هذه عادة جيدة، والأفضل أن تعيد الخاتمة ذكر الموقف نفسه.`,
      observation: ``,
    },
    {
      criterion: 'lexical_resource', kind: 'error', severity: 2,
      quote: `give benefit to`,
      noteEn: `The natural phrase is "bring benefits to" or the verb "benefit". A phrase like "give benefit to" is understood, but it sounds translated, and small collocation slips like this hold Lexical Resource back.`,
      noteAr: `العبارة الطبيعية هي "bring benefits to" أو الفعل "benefit". عبارة "give benefit to" مفهومة لكنها تبدو مترجمة حرفيًا، وهذه الهفوات الصغيرة في التلازم اللفظي تحدّ من الثروة اللغوية.`,
      fix: { kind: 'replace', text: `bring benefits to` },
      instances: [{ quote: `do exercise`, fix: { kind: 'replace', text: `take exercise` } }],
      observation: `Verb and noun combinations are not natural in two places.`,
      effect: `The meaning is clear, but the phrasing reads as translated.`,
    },
    {
      criterion: 'grammatical_range', kind: 'error', severity: 2,
      quote: `it keep`,
      noteEn: `"it" is singular, so the verb needs an "s": "it keeps". The same agreement and tense slip returns with "the government builds", "these facilities are" and "Qatar built", which holds Grammatical Range and Accuracy back.`,
      noteAr: `"it" مفردة، فيحتاج الفعل إلى "s": "it keeps". وتعود الهفوة نفسها في التطابق والزمن مع "the government builds" و"these facilities are" و"Qatar built"، وهذا يحدّ من المدى النحوي والدقة.`,
      fix: { kind: 'replace', text: `it keeps` },
      instances: [
        { quote: `the government build`, fix: { kind: 'replace', text: `the government builds` } },
        { quote: `these facilities is`, fix: { kind: 'replace', text: `these facilities are` } },
        { quote: `Qatar build`, fix: { kind: 'replace', text: `Qatar built` } },
      ],
      observation: `Subject-verb agreement and tense slip in several sentences, although the structures themselves (if-clauses, relative clauses) are controlled.`,
      effect: `Meaning stays clear, but the errors are frequent enough to be noticed.`,
    },
    {
      criterion: 'coherence_cohesion', kind: 'strength', severity: 1,
      quote: `Firstly, thousands of tourists come to the city and spend money in hotels and restaurants, so the economy grows.`,
      noteEn: `This paragraph opens with a clear signpost and follows the idea with its result, so the reader can follow the chain from tourists to spending to the economy. Keep building paragraphs this way.`,
      noteAr: `تبدأ الفقرة بكلمة ترتيب واضحة وتتبع الفكرة بنتيجتها، فيستطيع القارئ متابعة السلسلة من السياح إلى الإنفاق ثم الاقتصاد. بناء الفقرات بهذه الطريقة عادة جيدة.`,
      observation: `A clear signpost is followed by an idea and its result.`,
    },
    {
      criterion: 'coherence_cohesion', kind: 'error', severity: 2,
      quote: `and this attract foreign companies to invest`,
      noteEn: `"this" points back to a whole clause, so it is unclear whether it means the events or the fame they bring. Naming the idea, as in "this fame", gives the reader one clear reference.`,
      noteAr: `تشير "this" إلى جملة كاملة، فلا يتضح هل المقصود الأحداث أم الشهرة التي تجلبها. تسمية الفكرة، كما في "this fame"، تمنح القارئ إحالة واحدة واضحة.`,
      fix: { kind: 'replace', text: `and this fame attracts foreign companies to invest` },
      observation: `A demonstrative points back to a whole clause, so its reference is unclear.`,
      effect: `The reader has to decide what "this" refers to.`,
    },
    {
      criterion: 'grammatical_range', kind: 'error', severity: 1, alsoOf: `and this attract foreign companies to invest`,
      quote: `this attract`,
      noteEn: `"this" is singular, so the verb needs an "s": "this attracts". Agreement errors in the middle of longer sentences are what hold the Grammatical Range and Accuracy criterion down.`,
      noteAr: `"this" مفردة، فيحتاج الفعل بعدها إلى حرف "s" ليصبح "this attracts". أخطاء التطابق في وسط الجمل الأطول هي ما يحدّ من المدى النحوي والدقة.`,
      fix: { kind: 'replace', text: `this attracts` },
      observation: `A singular demonstrative is followed by a plural verb form.`,
    },
    {
      criterion: 'grammatical_range', kind: 'error', severity: 2,
      quote: `the new transport and stadiums are using again by the local people`,
      noteEn: `The passive needs "used", not "using": "are used again by local people". Mixing active and passive forms in one clause makes the meaning harder to follow and counts against accuracy.`,
      noteAr: `المبني للمجهول يحتاج إلى "used" لا "using": "are used again by local people". الخلط بين صيغتي الفاعل والمجهول في عبارة واحدة يصعّب فهم المعنى ويُحتسب على الدقة.`,
      fix: { kind: 'replace', text: `the new transport and stadiums are used again by local people` },
      observation: `A passive is built with the wrong participle.`,
      effect: `The reader has to repair the clause to follow it.`,
    },
    {
      criterion: 'task_achievement', kind: 'gap', severity: 2,
      quote: `However, I believe that the money must be shared more fairly.`,
      noteEn: `This sentence gives your main solution, but nothing explains how the money should be shared or who would benefit. An idea without a reason or an example counts as only partly developed.`,
      noteAr: `تعرض هذه الجملة الحل الرئيسي، لكن لا شيء يشرح كيفية توزيع المال ولا من سيستفيد منه. الفكرة بلا سبب أو مثال تُعد مطورة جزئيًا فقط.`,
      fix: {
        kind: 'instruction',
        en: `Add one sentence saying how: for example, a fixed share of each event budget for local clubs.`,
        ar: `إضافة جملة تبين الكيفية، مثل تخصيص حصة ثابتة من ميزانية كل حدث للأندية المحلية.`,
      },
      observation: ``,
    },
  ],
  evidence: {
    task_achievement: [`i partly agree with this opinion`, `However, I believe that the money must be shared more fairly`],
    coherence_cohesion: [`Firstly, thousands of tourists come to the city`, `On the other hand, international events can bring many advantages`],
    lexical_resource: [`give benefit to ordinary people`, `a huge amount of public money`],
    grammatical_range: [`it keep them away from the streets`, `the new transport and stadiums are using again`],
  },
  judgement: {
    task_achievement: `You state a clear position in the introduction and discuss both views in separate paragraphs. Your main solution in the fourth paragraph is stated but not supported with a reason or an example.`,
    coherence_cohesion: `Each paragraph has a clear job and the signposts help the reader. A few references are unclear, and ideas are often left undeveloped.`,
    lexical_resource: `You use topic vocabulary accurately. Some word combinations are not natural, and a few word choices repeat.`,
    grammatical_range: `You attempt a good range of structures, including conditionals and relative clauses. Agreement and tense errors appear in most of the longer sentences.`,
  },
  reasons: {
    task_achievement: { en: `You give a clear position and discuss both sides, but your main solution in the fourth paragraph needs an example or a reason.`, ar: `موقفك واضح وقد ناقشت الجانبين، لكن الحل الرئيسي في الفقرة الرابعة يحتاج إلى مثال أو سبب.` },
    coherence_cohesion: { en: `Each paragraph has a clear job and the signposts help, but some references are unclear and ideas need more development.`, ar: `لكل فقرة وظيفة واضحة وأدوات الترتيب مفيدة، لكن بعض الإحالات غير واضحة والأفكار تحتاج إلى مزيد من التطوير.` },
    lexical_resource: { en: `You use topic vocabulary accurately, but some word combinations are not natural.`, ar: `مفردات الموضوع مستخدمة بدقة، لكن بعض التراكيب اللفظية غير طبيعية.` },
    grammatical_range: { en: `You use a good range of structures, but agreement and tense errors appear in most longer sentences.`, ar: `تنوع البنى النحوية جيد، لكن أخطاء التطابق والأزمنة تظهر في معظم الجمل الأطول.` },
  },
  nextActions: [
    {
      criterion: 'grammatical_range',
      en: `In your longer sentences, check every verb against its subject and its tense before you move on.`,
      ar: `في المقال القادم: مطابقة كل فعل مع فاعله وزمنه في الجمل الطويلة قبل الانتقال إلى الفقرة التالية.`,
    },
    {
      criterion: 'coherence_cohesion',
      en: `When you use "this" or "it", check that it points to one clear noun, and add an example to each main idea.`,
      ar: `في المقال القادم: التأكد من أن "this" أو "it" تشير إلى اسم واحد واضح، وإضافة مثال إلى كل فكرة رئيسية.`,
    },
  ],
  recommendations: `Develop each main idea with a reason or an example. Vary your sentence structures and proofread for agreement, article and verb-form errors before you finish.`,
}

export const DEMO_ESSAYS: Record<TaskNumber, EssayContent> = { 1: TASK1, 2: TASK2 }

/* -------------------------------------------------------------------------- */
/* `/review/` evidence: details.criteria and details.language_observations     */
/* -------------------------------------------------------------------------- */

export function criteriaEvidence(content: EssayContent, bands: TaskBands) {
  return Object.fromEntries(CRITERIA.map((criterion) => [criterion, {
    evidence_quotes: content.evidence[criterion],
    judgement: content.judgement[criterion],
    band: bands[criterion],
  }]))
}

/** The ledger exactly as `writing_language_observations.py` stores it: `{ledger, quote_locations}` (offsets are code points). */
export function languageObservations(content: EssayContent) {
  const text = essayText(content)
  const ledger: Record<string, { strengths: Array<{ quote: string; observation: string }>; limitations: Array<Record<string, unknown>>; coverage_note: string }> = {}
  const locations: Record<string, Array<{ start: number; end: number }>> = {}
  const where = (quote: string) => occurrences(text, quote).map((span) => ({ start: cp(text, span.start), end: cp(text, span.end) }))
  for (const criterion of LEDGER_CRITERIA) {
    const shard = { strengths: [] as Array<{ quote: string; observation: string }>, limitations: [] as Array<Record<string, unknown>>, coverage_note: `All sentences inspected.` }
    for (const finding of content.findings.filter((entry) => entry.criterion === criterion)) {
      if (finding.kind === 'strength') {
        locations[`${criterion}.strengths.${shard.strengths.length}.quote`] = where(finding.quote)
        shard.strengths.push({ quote: finding.quote, observation: finding.observation })
        continue
      }
      const quotes = [finding.quote, ...(finding.instances ?? []).map((instance) => instance.quote)]
      const index = shard.limitations.length
      quotes.forEach((quote, j) => { locations[`${criterion}.limitations.${index}.quotes.${j}`] = where(quote) })
      shard.limitations.push({
        quotes,
        observation: finding.observation,
        scope: quotes.length > 1 ? 'distributed' : 'local',
        recurrence: 'avoidable',
        communication_effect: finding.effect ?? '',
        counterevidence_strength_indices: [],
        counterevidence_note: '',
      })
    }
    ledger[criterion] = shard
  }
  return { ledger, quote_locations: locations }
}

/* -------------------------------------------------------------------------- */
/* `/feedback/` payload for one task                                           */
/* -------------------------------------------------------------------------- */

export interface FeedbackTaskOptions {
  /** `observations` (full, default), or `none`: a script graded before the evidence existed (no annotations). */
  profile?: 'observations' | 'none'
  /** Criteria whose reason is null (the page then uses the band-range descriptor line). */
  nullReasons?: FeedbackCriterion[]
  lockedAt: string
  generatedAt: string
}

export function buildTaskFeedback(content: EssayContent, bands: TaskBands, options: FeedbackTaskOptions): TaskFeedback {
  const text = essayText(content)
  const profile = options.profile ?? 'observations'
  const taskPrefix = `t${content.taskNumber}`

  const mergedNote = (finding: Finding): FeedbackMergedNote => ({
    criterion: finding.criterion, kind: finding.kind, severity: finding.severity,
    ...spanOf(text, finding.quote), note_en: finding.noteEn, note_ar: finding.noteAr, ...fixFields(finding.fix),
  })
  const instanceOf = (instance: { quote: string; fix: Fix }): FeedbackInstance => ({ ...spanOf(text, instance.quote), ...fixFields(instance.fix) })

  const primaries = content.findings.filter((finding) => !finding.alsoOf)
    .map((finding) => ({ finding, span: spanOf(text, finding.quote) }))
    .sort((a, b) => a.span.start - b.span.start)

  const annotations: FeedbackAnnotation[] = profile === 'none' ? [] : primaries.map(({ finding }, index) => ({
    id: `${taskPrefix}-a${index + 1}`,
    ...mergedNote(finding),
    instances: (finding.instances ?? []).map(instanceOf),
    source: 'ai',
    verified: true,
    order: index + 1,
    also: content.findings.filter((entry) => entry.alsoOf === finding.quote).map(mergedNote),
  }))

  return {
    task_number: content.taskNumber,
    question_id: content.questionId,
    status: 'ready',
    unavailable_reason: null,
    error_code: null,
    feedback_version: 'writing-feedback-v1',
    locked_at: options.lockedAt,
    generated_at: options.generatedAt,
    evidence_profile: profile === 'none' ? 'none' : 'observations',
    essay: { text, length_utf16: text.length, word_count: wordCount(text) },
    criteria: CRITERIA.map((criterion) => ({
      criterion,
      band: bands[criterion],
      reason_en: options.nullReasons?.includes(criterion) ? null : content.reasons[criterion].en,
      reason_ar: options.nullReasons?.includes(criterion) ? null : content.reasons[criterion].ar,
    })),
    annotations,
    next_actions: content.nextActions.map((action) => ({ criterion: action.criterion, text_en: action.en, text_ar: action.ar })),
  }
}
